import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { db, TrainingDay } from '../database';

interface TrainingDayWithExerciseCount extends TrainingDay {
  exerciseCount: number;
}

const RoutineForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = id && id !== 'new';

  const [name, setName] = useState('');
  const [trainingDays, setTrainingDays] = useState<TrainingDayWithExerciseCount[]>([]);
  const [selectedDayIds, setSelectedDayIds] = useState<number[]>([]);

  const loadRoutine = useCallback(async () => {
    if (!isEditing) return; // Don't try to load for new routines
    
    try {
      const routineId = parseInt(id!);
      if (isNaN(routineId)) return; // Invalid ID
      
      const routine = await db.routines.get(routineId);
      if (routine) {
        setName(routine.name);
        setSelectedDayIds(routine.trainingDayIds);
      }
    } catch (error) {
      console.error('Failed to load routine:', error);
    }
  }, [id, isEditing]);

  useEffect(() => {
    loadTrainingDays();
    if (isEditing) {
      loadRoutine();
    }
  }, [id, isEditing, loadRoutine]);

  const loadTrainingDays = async () => {
    try {
      const days = await db.trainingDays.orderBy('name').toArray();
      
      // Load exercise counts for each training day
      const daysWithCounts = await Promise.all(
        days.map(async (day) => {
          const exerciseCount = await db.trainingDayExercises
            .where('trainingDayId')
            .equals(day.id!)
            .count();
          
          return {
            ...day,
            exerciseCount
          };
        })
      );
      
      setTrainingDays(daysWithCounts);
    } catch (error) {
      console.error('Failed to load training days:', error);
    }
  };

  const toggleTrainingDay = (dayId: number) => {
    if (selectedDayIds.includes(dayId)) {
      setSelectedDayIds(selectedDayIds.filter(id => id !== dayId));
    } else {
      setSelectedDayIds([...selectedDayIds, dayId]);
    }
  };

  const moveDay = (index: number, direction: 'up' | 'down') => {
    const newOrder = [...selectedDayIds];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex >= 0 && targetIndex < newOrder.length) {
      [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
      setSelectedDayIds(newOrder);
    }
  };

  const handleSave = async () => {
    if (!name.trim() || selectedDayIds.length === 0) return;

    try {
      if (isEditing) {
        await db.routines.update(parseInt(id!), {
          name: name.trim(),
          trainingDayIds: selectedDayIds,
          updatedAt: new Date()
        });
      } else {
        await db.routines.add({
          name: name.trim(),
          trainingDayIds: selectedDayIds,
          currentDayIndex: 0,
          active: true,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }

      navigate('/routines');
    } catch (error) {
      console.error('Failed to save routine:', error);
    }
  };

  const getTrainingDayName = (id: number) => {
    return trainingDays.find(day => day.id === id)?.name || 'Unknown';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <button
          onClick={() => navigate('/routines')}
          className="text-blue-600"
        >
          ← Back
        </button>
        <h2 className="text-xl font-bold text-gray-900">
          {isEditing ? 'Edit Routine' : 'New Routine'}
        </h2>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Routine Name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded"
          placeholder="e.g., Push/Pull/Legs"
        />
      </div>

      {trainingDays.length === 0 ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            No training days available. Create some training days first.
          </p>
          <Link
            to="/training-days/new"
            className="text-blue-600 text-sm mt-2 inline-block"
          >
            Create Training Day →
          </Link>
        </div>
      ) : (
        <>
          <div>
            <h3 className="font-medium text-gray-900 mb-3">
              Select Training Days for Cycle
            </h3>
            <div className="space-y-2">
              {trainingDays.map((day) => (
                <label
                  key={day.id}
                  className="flex items-center p-3 border rounded cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={selectedDayIds.includes(day.id!)}
                    onChange={() => toggleTrainingDay(day.id!)}
                    className="mr-3"
                  />
                  <div>
                    <div className="font-medium">{day.name}</div>
                    <div className="text-sm text-gray-500">
                      {day.exerciseCount} {day.exerciseCount === 1 ? 'exercise' : 'exercises'}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {selectedDayIds.length > 0 && (
            <div>
              <h3 className="font-medium text-gray-900 mb-3">
                Cycle Order (Day 1 → Day 2 → Day 3...)
              </h3>
              <div className="space-y-2">
                {selectedDayIds.map((dayId, index) => (
                  <div
                    key={dayId}
                    className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded"
                  >
                    <div>
                      <span className="text-sm text-blue-600 font-medium">
                        Day {index + 1}:
                      </span>
                      <span className="ml-2">{getTrainingDayName(dayId)}</span>
                    </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => moveDay(index, 'up')}
                        disabled={index === 0}
                        className="p-1 text-blue-600 disabled:text-gray-400"
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => moveDay(index, 'down')}
                        disabled={index === selectedDayIds.length - 1}
                        className="p-1 text-blue-600 disabled:text-gray-400"
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <div className="flex space-x-3 relative z-10">
        <button
          onClick={handleSave}
          className="flex-1 bg-blue-600 text-white py-2 rounded cursor-pointer hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          disabled={!name.trim() || selectedDayIds.length === 0}
          style={{ pointerEvents: 'auto' }}
        >
          Save Routine
        </button>
        <button
          onClick={() => navigate('/routines')}
          className="flex-1 bg-gray-300 text-gray-700 py-2 rounded cursor-pointer hover:bg-gray-400"
          style={{ pointerEvents: 'auto' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default RoutineForm;